import { Injectable, signal } from '@angular/core'

/**
 * GBC Design System Configuration
 * Type-safe access to design tokens from Figma
 */

export interface ColorPalette {
  50: string
  100: string
  200: string
  300: string
  400: string
  500: string
  600: string
  700: string
  800: string
  900: string
}

export interface StateColors {
  error: string
  warning: string
  info: string
  success: string
}

export interface GrayscaleColors {
  1: string
  2: string
  3: string
  4: string
}

export interface DesignTokens {
  colors: {
    primary: ColorPalette
    secondary: ColorPalette
    support: ColorPalette
    error: ColorPalette
    warning: ColorPalette
    info: ColorPalette
    success: ColorPalette
    dark: GrayscaleColors
    light: GrayscaleColors
    state: StateColors
  }
  spacing: Record<string, string>
  typography: {
    fontFamily: Record<string, string[]>
    fontSize: Record<string, string>
    fontWeight: Record<string, string>
    lineHeight: Record<string, string>
  }
}

/**
 * Design System Service
 *
 * Provides type-safe access to design tokens and theme management.
 * Integrates with Angular Signals for reactive theme switching.
 *
 * Usage:
 * ```typescript
 * constructor(private designSystem: DesignSystemService) {}
 *
 * // Get colors
 * const primaryColor = this.designSystem.getColor('primary', 500);
 * const errorColor = this.designSystem.getStateColor('error');
 *
 * // Theme management
 * this.designSystem.setTheme('dark');
 * const currentTheme = this.designSystem.currentTheme();
 * ```
 */
@Injectable({ providedIn: 'root' })
export class DesignSystemService {
  private readonly _currentTheme = signal<'light' | 'dark'>('light')

  readonly currentTheme = this._currentTheme.asReadonly()

  /**
   * GBC Design Tokens from Figma
   */
  private readonly tokens: DesignTokens = {
    colors: {
      primary: {
        50: '#f0f0f7',
        100: '#e8e8ef',
        200: '#d1d1df',
        300: '#b3b3c7',
        400: '#8f8faf',
        500: '#23235b', // Main primary (80% usage)
        600: '#1f1f52',
        700: '#1a1a46',
        800: '#16163a',
        900: '#12122d', // Foncé
      },
      secondary: {
        50: '#f0f0f7',
        100: '#e0e0ee',
        200: '#c1c1dd',
        300: '#9999bb',
        400: '#666699',
        500: '#12122d', // Main secondary (15% usage)
        600: '#101028',
        700: '#0e0e23',
        800: '#0c0c1e',
        900: '#0a0a1a',
      },
      support: {
        50: '#faf7f2',
        100: '#f5efe5',
        200: '#ebdfcb',
        300: '#e0cfb1',
        400: '#d6a65f', // Main support (5% usage)
        500: '#b8904f',
        600: '#9a7a3f',
        700: '#7c642f',
        800: '#5e4e1f',
        900: '#40380f',
      },
      error: {
        50: '#fff5f5',
        100: '#fed7d7',
        200: '#feb2b2',
        300: '#fc8181',
        400: '#f56565',
        500: '#ff3b30',
        600: '#e53e3e',
        700: '#c53030',
        800: '#9b2c2c',
        900: '#742a2a',
      },
      warning: {
        50: '#fffbf0',
        100: '#fef5e7',
        200: '#feebc8',
        300: '#fbd38d',
        400: '#f6ad55',
        500: '#ff9500',
        600: '#dd6b20',
        700: '#c05621',
        800: '#9c4221',
        900: '#7b341e',
      },
      info: {
        50: '#ebf8ff',
        100: '#bee3f8',
        200: '#90cdf4',
        300: '#63b3ed',
        400: '#4299e1',
        500: '#0066f7',
        600: '#3182ce',
        700: '#2c5282',
        800: '#2a4365',
        900: '#1a365d',
      },
      success: {
        50: '#f0fff4',
        100: '#c6f6d5',
        200: '#9ae6b4',
        300: '#68d391',
        400: '#48bb78',
        500: '#30d158',
        600: '#38a169',
        700: '#2f855a',
        800: '#276749',
        900: '#22543d',
      },
      dark: {
        1: '#494a3c',
        2: '#6b7280',
        3: '#9f9ca6',
        4: '#c7c3c9',
      },
      light: {
        1: '#d0d5e9',
        2: '#e8e8f0',
        3: '#f2f2f5',
        4: '#fafafc',
      },
      state: {
        error: '#ff3b30',
        warning: '#ff9500',
        info: '#0066f7',
        success: '#30d158',
      },
    },
    spacing: {
      xs: '0.25rem', // 4px
      sm: '0.5rem', // 8px
      md: '1rem', // 16px
      lg: '1.5rem', // 24px
      xl: '2rem', // 32px
      '2xl': '3rem', // 48px
      '3xl': '4rem', // 64px
    },
    typography: {
      fontFamily: {
        display: ['Inter', 'system-ui', 'sans-serif'],
        body: ['Mulish', 'Inter', 'system-ui', 'sans-serif'],
      },
      fontSize: {
        // Display styles
        'display-1': '48px',
        'display-2': '40px',

        // Heading styles
        'heading-1': '44px',
        'heading-2': '36px',
        'heading-3': '28px',
        'heading-4': '24px',
        'heading-5': '20px',
        'heading-6': '16px',

        // Body styles
        lead: '22px',
        'body-large': '20px',
        'body-medium': '18px',
        'body-normal': '16px',
        'body-small': '14px',
      },
      fontWeight: {
        'extra-light': '200',
        regular: '400',
        semibold: '600',
        bold: '700',
      },
      lineHeight: {
        auto: 'auto',
      },
    },
  }

  /**
   * Gets a color from the design system.
   * @param palette Color palette name
   * @param shade Color shade (50-900)
   * @returns Hex color value
   */
  getColor(palette: keyof Omit<DesignTokens['colors'], 'dark' | 'light' | 'state'>, shade: keyof ColorPalette): string {
    return this.tokens.colors[palette][shade]
  }

  /**
   * Gets a state color.
   * @param state State color name
   * @returns Hex color value
   */
  getStateColor(state: keyof StateColors): string {
    return this.tokens.colors.state[state]
  }

  /**
   * Gets a grayscale color.
   * @param type 'dark' or 'light'
   * @param shade Shade number (1-4)
   * @returns Hex color value
   */
  getGrayscaleColor(type: 'dark' | 'light', shade: keyof GrayscaleColors): string {
    return this.tokens.colors[type][shade]
  }

  /**
   * Gets a spacing value.
   * @param size Spacing size name
   * @returns CSS spacing value
   */
  getSpacing(size: string): string {
    return this.tokens.spacing[size] || size
  }

  /**
   * Gets a typography value.
   * @param category Typography category
   * @param property Property name
   * @returns Typography value
   */
  getTypography(category: keyof DesignTokens['typography'], property: string): string | string[] {
    return this.tokens.typography[category][property]
  }

  /**
   * Sets the current theme.
   * @param theme Theme name
   */
  setTheme(theme: 'light' | 'dark'): void {
    this._currentTheme.set(theme)
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem('gbc-theme', theme)
  }

  /**
   * Toggles between light and dark theme.
   */
  toggleTheme(): void {
    const newTheme = this._currentTheme() === 'light' ? 'dark' : 'light'
    this.setTheme(newTheme)
  }

  /**
   * Initializes the theme from localStorage or system preference.
   */
  initializeTheme(): void {
    const savedTheme = localStorage.getItem('gbc-theme') as 'light' | 'dark'
    if (savedTheme) {
      this.setTheme(savedTheme)
    } else {
      // Detect system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      this.setTheme(prefersDark ? 'dark' : 'light')
    }
  }

  /**
   * Gets the complete design tokens object.
   * @returns Design tokens
   */
  getTokens(): DesignTokens {
    return this.tokens
  }

  /**
   * Generates CSS custom properties from design tokens.
   * @returns CSS custom properties string
   */
  generateCSSCustomProperties(): string {
    const css: string[] = []

    // Colors
    Object.entries(this.tokens.colors).forEach(([palette, colors]) => {
      if (typeof colors === 'object' && colors !== null) {
        Object.entries(colors).forEach(([shade, color]) => {
          css.push(`--color-${palette}-${shade}: ${color};`)
        })
      }
    })

    // Spacing
    Object.entries(this.tokens.spacing).forEach(([size, value]) => {
      css.push(`--spacing-${size}: ${value};`)
    })

    return css.join('\n')
  }
}
